import { createFileRoute, Outlet, redirect, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { MemberShell } from "@/components/member-shell";
import { getMyAccessStatus } from "@/lib/admin.functions";
import { CompleteRegistrationScreen } from "@/components/complete-registration-screen";
import { MembershipBlockedScreen } from "@/components/membership-blocked-screen";

export const ACCESS_STATUS_QUERY = {
  queryKey: ["access-status"],
  queryFn: () => getMyAccessStatus(),
  staleTime: 30_000,
  retry: 1,
} as const;

export const Route = createFileRoute("/_authenticated")({
  ssr: false,
  beforeLoad: async () => {
    const { data, error } = await supabase.auth.getUser();
    if (error || !data.user) throw redirect({ to: "/auth" });
    return { user: data.user };
  },
  // Buscar a situação de acesso antes de renderizar evita a tela intermediária
  // de carregamento (e o "salto" do conteúdo depois dela).
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(ACCESS_STATUS_QUERY).catch(() => null);
  },
  component: AuthenticatedShell,
});

function AuthenticatedShell() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const fn = useServerFn(getMyAccessStatus);
  const { data, isLoading, isError, error, refetch } = useQuery({
    ...ACCESS_STATUS_QUERY,
    queryFn: () => fn(),
  });

  // Suporte deve continuar acessível mesmo pendente.
  const isSupport = pathname.startsWith("/suporte");

  if (isLoading) {
    // Já mostramos o cabeçalho/rodapé definitivos com um espaço reservado no
    // meio: assim o conteúdo aparece no lugar certo, sem "pular" a página.
    return (
      <MemberShell>
        <div
          className="min-h-[70vh] animate-pulse rounded-xl bg-secondary"
          role="status"
          aria-label="Carregando"
        />
      </MemberShell>
    );
  }

  // Sem este tratamento a tela ficaria presa no indicador de carregamento
  // quando a verificação de acesso falha (rede, backend fora do ar).
  if (isError || !data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center shadow-sm">
          <h1 className="font-serif text-xl font-semibold text-primary">
            Não foi possível verificar seu acesso
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            {error instanceof Error && error.message
              ? error.message
              : "Falha de comunicação com o servidor."}{" "}
            Tente novamente em alguns instantes.
          </p>
          <button
            onClick={() => void refetch()}
            className="mt-6 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  const status = (data as any).status as string | undefined;
  const blockReason = (data as any).blockReason as string | null;

  // Autenticado não é autorizado: sem cadastro, em análise ou reprovado ficam
  // fora da área de membros (o suporte continua acessível).
  if (!data.approved && !isSupport) {
    if ((data as any).hasProfile === false) return <MembershipBlockedScreen state="none" />;
    if (status === "rejected" || blockReason === "cadastro_reprovado")
      return <MembershipBlockedScreen state="rejected" />;
    if (!blockReason && status === "pending") return <MembershipBlockedScreen state="pending" />;
    return (
      <CompleteRegistrationScreen
        missing={(data as any).missing ?? []}
        fullName={(data as any).fullName}
        cpf={(data as any).cpf}
        blockReason={blockReason ?? null}
        blockDetail={(data as any).blockDetail ?? null}
      />
    );
  }

  return (
    <MemberShell>
      <Outlet />
    </MemberShell>
  );
}
